import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Link as RouterLink } from 'react-router-dom';
import { formatDateInIST } from '../../../utils/reportExport';
import {
  CRITICALITY_TONES,
  columnText,
  columnsForMode,
  dueBucketFor,
} from '../../../utils/legalUpdateReportView';

const EXPANDER_WIDTH = 46;
const MAX_ROWS_PER_OPEN_GROUP = 300;

const ClampedText = ({ text, lines = 2 }) => (
  <Tooltip title={text} placement="top-start" enterDelay={600}>
    <Box
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Box>
  </Tooltip>
);

const DueDateCell = ({ value, now }) => {
  const bucket = dueBucketFor(value, now);
  const formatted = formatDateInIST(value);
  if (!formatted) {
    return (
      <Typography variant="body2" color="text.disabled">
        -
      </Typography>
    );
  }
  return (
    <Stack spacing={0.35} alignItems="flex-start">
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {formatted}
      </Typography>
      <Chip
        size="small"
        label={bucket.chipLabel}
        color={bucket.tone === 'default' ? 'default' : bucket.tone}
        variant={bucket.id === 'overdue' ? 'filled' : 'outlined'}
        sx={{ height: 19, fontSize: 11, fontWeight: 700 }}
      />
    </Stack>
  );
};

const renderCell = (column, row, now) => {
  const text = columnText(column, row);

  switch (column.type) {
    case 'dueDate':
      return <DueDateCell value={row[column.id]} now={now} />;
    case 'date':
      return text || <Typography component="span" color="text.disabled">-</Typography>;
    case 'criticality':
      return text ? (
        <Chip
          size="small"
          label={text}
          color={CRITICALITY_TONES[text] || 'default'}
          variant="outlined"
          sx={{ height: 22, fontWeight: 700 }}
        />
      ) : (
        <Typography component="span" color="text.disabled">-</Typography>
      );
    case 'status':
      return text ? (
        <Chip size="small" label={text} variant="outlined" sx={{ height: 22, maxWidth: '100%' }} />
      ) : (
        <Typography component="span" color="text.disabled">-</Typography>
      );
    case 'legalUpdateLink':
      if (!text) return <Typography component="span" color="text.disabled">-</Typography>;
      return row.legalUpdateId ? (
        <Link
          component={RouterLink}
          to={`/legal-update/${row.legalUpdateId}`}
          underline="hover"
          sx={{ fontWeight: 600 }}
        >
          <ClampedText text={text} />
        </Link>
      ) : (
        <ClampedText text={text} />
      );
    case 'trackerLink':
      if (!text) return <Typography component="span" color="text.disabled">-</Typography>;
      return row.trackerLink ? (
        <Link href={row.trackerLink} underline="hover" sx={{ fontWeight: 600 }}>
          <ClampedText text={text} />
        </Link>
      ) : (
        <ClampedText text={text} />
      );
    case 'people':
      return text ? <ClampedText text={text} /> : (
        <Typography component="span" color="text.disabled">-</Typography>
      );
    default:
      return text ? <ClampedText text={text} /> : (
        <Typography component="span" color="text.disabled">-</Typography>
      );
  }
};

const DetailPanel = ({ row, mode, now }) => {
  const allColumns = columnsForMode(mode);
  const links = [
    row.legalUpdateId ? { label: 'Legal update', to: `/legal-update/${row.legalUpdateId}` } : null,
    row.trackerLink ? { label: 'Open task', href: row.trackerLink } : null,
    row.sourceURL ? { label: 'Source URL', href: row.sourceURL } : null,
  ].filter(Boolean);

  return (
    <Box sx={{ px: 2, py: 1.75, backgroundColor: 'rgba(46,125,50,0.035)' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}
      >
        {allColumns.map((column) => (
          <Box key={column.id}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}
            >
              {column.label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {column.type === 'dueDate'
                ? [formatDateInIST(row[column.id]), dueBucketFor(row[column.id], now).chipLabel]
                  .filter(Boolean)
                  .join(' - ') || '-'
                : columnText(column, row) || '-'}
            </Typography>
          </Box>
        ))}
      </Box>
      {links.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
          {links.map((link) => (
            <Link
              key={link.label}
              component={link.to ? RouterLink : 'a'}
              to={link.to}
              href={link.href}
              target={link.href ? '_blank' : undefined}
              rel={link.href ? 'noopener noreferrer' : undefined}
              underline="hover"
              sx={{ fontWeight: 700, fontSize: 13 }}
            >
              {link.label}
            </Link>
          ))}
        </Stack>
      )}
    </Box>
  );
};

const ReportResultsTable = ({
  mode,
  columns,
  rows,
  groups,
  sort,
  onSortChange,
  density,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  now,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const compact = density === 'compact';
  const cellPadding = compact ? { py: 0.5, px: 1.25 } : { py: 1.1, px: 1.5 };
  const totalColumns = columns.length + 1;

  useEffect(() => {
    setOpenGroups({});
    setExpandedRows({});
  }, [groups, mode]);

  const pagedRows = useMemo(() => {
    if (groups) return [];
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [groups, rows, page, rowsPerPage]);

  const toggleRow = (rowId) =>
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));

  const stickyCellSx = (offset, isHeader) => ({
    position: 'sticky',
    left: offset,
    zIndex: isHeader ? 4 : 2,
    backgroundColor: 'inherit',
    borderRight: '1px solid',
    borderRightColor: 'divider',
  });

  const renderRow = (row) => {
    const isOpen = Boolean(expandedRows[row._rowId]);
    return (
      <Fragment key={row._rowId}>
        <TableRow
          hover
          sx={{
            backgroundColor: isOpen ? 'rgba(46,125,50,0.06)' : '#fff',
            '&:nth-of-type(even)': { backgroundColor: isOpen ? 'rgba(46,125,50,0.06)' : '#fafafa' },
            '&:hover': { backgroundColor: 'rgba(46,125,50,0.08)' },
          }}
        >
          <TableCell
            sx={{
              ...cellPadding,
              width: EXPANDER_WIDTH,
              minWidth: EXPANDER_WIDTH,
              maxWidth: EXPANDER_WIDTH,
              ...stickyCellSx(0, false),
              borderRight: 'none',
            }}
          >
            <IconButton size="small" onClick={() => toggleRow(row._rowId)}>
              {isOpen ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </IconButton>
          </TableCell>
          {columns.map((column) => (
            <TableCell
              key={column.id}
              sx={{
                ...cellPadding,
                minWidth: column.minWidth,
                maxWidth: column.minWidth * 1.6,
                verticalAlign: 'top',
                ...(column.sticky ? stickyCellSx(EXPANDER_WIDTH, false) : {}),
              }}
            >
              {renderCell(column, row, now)}
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell colSpan={totalColumns} sx={{ p: 0, border: 0 }}>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <DetailPanel row={row} mode={mode} now={now} />
            </Collapse>
          </TableCell>
        </TableRow>
      </Fragment>
    );
  };

  const renderGroups = () =>
    groups.map((group) => {
      const isOpen = Boolean(openGroups[group.key]);
      const visible = group.rows.slice(0, MAX_ROWS_PER_OPEN_GROUP);
      const hidden = group.rows.length - visible.length;
      return (
        <Fragment key={group.key}>
          <TableRow
            hover
            onClick={() => setOpenGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
            sx={(theme) => ({
              cursor: 'pointer',
              backgroundColor: alpha(theme.palette.success.main, 0.08),
              '& > td': { borderBottom: '1px solid', borderColor: 'divider' },
            })}
          >
            <TableCell colSpan={totalColumns} sx={{ ...cellPadding, py: 0.75 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {isOpen ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {group.key}
                </Typography>
                <Chip
                  size="small"
                  label={group.rows.length.toLocaleString()}
                  sx={{ height: 20, fontWeight: 700 }}
                />
              </Stack>
            </TableCell>
          </TableRow>
          {isOpen && visible.map(renderRow)}
          {isOpen && hidden > 0 && (
            <TableRow>
              <TableCell colSpan={totalColumns} sx={{ ...cellPadding }}>
                <Typography variant="caption" color="text.secondary">
                  {hidden.toLocaleString()} more rows in this group. Narrow the filters or search to
                  see them.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </Fragment>
      );
    });

  const headerSortProps = (column) => ({
    active: sort.columnId === column.id,
    direction: sort.columnId === column.id && sort.direction ? sort.direction : 'asc',
    onClick: () => onSortChange(column.id),
  });

  return (
    <Box>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxHeight: 'calc(100vh - 260px)',
          minHeight: 280,
          backgroundColor: '#fff',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  backgroundColor: '#f1f5f2',
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  borderBottom: '2px solid',
                  borderBottomColor: 'divider',
                },
              }}
            >
              <TableCell
                sx={{
                  ...cellPadding,
                  width: EXPANDER_WIDTH,
                  minWidth: EXPANDER_WIDTH,
                  ...stickyCellSx(0, true),
                  borderRight: 'none',
                }}
              />
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sortDirection={sort.columnId === column.id ? sort.direction : false}
                  sx={{
                    ...cellPadding,
                    minWidth: column.minWidth,
                    ...(column.sticky ? stickyCellSx(EXPANDER_WIDTH, true) : {}),
                  }}
                >
                  <TableSortLabel {...headerSortProps(column)}>{column.label}</TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColumns} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No rows match the current view. Clear the search or filters to see more.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : groups ? (
              renderGroups()
            ) : (
              pagedRows.map(renderRow)
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {groups ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {groups.length.toLocaleString()} groups. Click a group to expand it.
        </Typography>
      ) : (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_event, nextPage) => onPageChange(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
          rowsPerPageOptions={[25, 50, 100, 250]}
        />
      )}
    </Box>
  );
};

export default ReportResultsTable;
